import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, FolderOpen, Loader2, ChevronRight } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { documentService, TipoDocumento } from "../../services/documentService";

export function Documentos() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [tipos, setTipos] = useState<TipoDocumento[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) navigate("/area-cliente");
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    loadTipos();
  }, []);

  const loadTipos = async () => {
    setIsLoading(true);
    try {
      const stats = await documentService.getStats();
      // Só exibe tipos que possuem ao menos um documento publicado para o cliente
      setTipos(stats.filter((t) => (t.count ?? 0) > 0));
    } catch (error) {
      console.error("Erro ao carregar documentos:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Documentos | Portal do Cliente</title>
      </Helmet>

      <div className="min-h-screen">
        <div className="py-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Link
              to="/portal"
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar ao Portal
            </Link>
            <h1 className="text-3xl md:text-4xl font-bold">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_auto] animate-[gradient_3s_linear_infinite]">
                Documentos
              </span>
            </h1>
            <p className="text-muted-foreground mt-1">
              Notas fiscais, boletos e demais documentos publicados para sua empresa
            </p>
          </motion.div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-brand animate-spin" />
            </div>
          ) : tipos.length === 0 ? (
            <div className="text-center py-16 rounded-xl bg-card border border-border">
              <FolderOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Nenhum documento publicado até o momento</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {tipos.map((tipo, index) => (
                <motion.div
                  key={tipo.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={`/portal/documentos/${tipo.id}`}
                    className="group relative bg-card/50 backdrop-blur-sm border border-border/50 hover:border-brand/50 rounded-xl p-6 transition-all duration-300 hover:shadow-lg flex flex-col h-full"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-brand/20 to-blue-500/20 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-brand" />
                      </div>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                        {tipo.count} documento{tipo.count === 1 ? "" : "s"}
                      </span>
                    </div>
                    <h3 className="font-semibold text-foreground group-hover:text-brand transition-colors">
                      {tipo.nome}
                    </h3>
                    {tipo.descricao && (
                      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                        {tipo.descricao}
                      </p>
                    )}
                    <div className="flex items-center gap-1 text-brand text-sm mt-auto pt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      Ver documentos
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
